import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import type { ChatInputContext } from './useDrawerAskAIComposerContext'
import {
  buildAskAIContextForDay,
  buildAskAIContextForItem,
  buildAskAIContextForListRow,
} from '../utils/buildAskAIContext'

interface UseAskAIContextChipArgs {
  /** When false, Ask AI highlight events are ignored (e.g. composer not mounted/visible). */
  enabled: boolean
  /** Called after a context chip is applied (typically focus the local input). */
  onActivate?: () => void
}

interface UseAskAIContextChipReturn {
  askAIContext: ChatInputContext | undefined
  clearAskAIContext: () => void
}

export function useAskAIContextChip({
  enabled,
  onActivate,
}: UseAskAIContextChipArgs): UseAskAIContextChipReturn {
  const intl = useIntl()
  const [askAIContext, setAskAIContext] = useState<ChatInputContext | undefined>(undefined)

  const clearAskAIContext = useCallback(() => {
    setAskAIContext(undefined)
  }, [])

  useEventSubscription('ask_ai_for_day', (event) => {
    if (!enabled) return
    setAskAIContext(buildAskAIContextForDay(intl, event))
    onActivate?.()
  })

  useEventSubscription('ask_ai_for_item', (event) => {
    if (!enabled) return
    setAskAIContext(buildAskAIContextForItem(intl, event))
    onActivate?.()
  })

  useEventSubscription('ask_ai_for_list_row', (event) => {
    if (!enabled) return
    setAskAIContext(buildAskAIContextForListRow(intl, event))
    onActivate?.()
  })

  useEventSubscription('reset_chat_state', () => {
    setAskAIContext(undefined)
  })

  return {
    askAIContext,
    clearAskAIContext,
  }
}
